/**
 * Геометрия проводки: где кабель начинается, как идёт и сколько его нужно.
 *
 * Кабель — ломаная по потолку между двумя приборами слоя электрики. Прибор — либо
 * на стене (розетка, выключатель, распредкоробка, бра), либо на потолке (люстра,
 * спот). Концы не хранятся: точка прибора на стене считается из стены (грань,
 * смещение), точка светильника — это он сам, поэтому передвинул прибор — трасса
 * потянулась за ним. К прибору на стене кабель спускается с потолка на высоту
 * установки; к потолочному светильнику спуска нет — он уже на потолке.
 *
 * Здесь же правила черчения: кабель идёт только под прямыми углами, как его
 * и кладут по потолку в штробах или лотках.
 *
 * Все размеры — в миллиметрах.
 */
import { dist, snapTo, type Pt } from "@/shared/lib/geometry";
import { fixturePlanPoint, type Cable, type ItemsLayer, type WallGraph } from "@/shared/plan";

export { fixturePlanPoint } from "@/shared/plan";

/** Точка конца кабеля на плане: прибор на стене — по стене, светильник на потолке — где стоит. */
export const cableEndPoint = (graph: WallGraph, electrical: ItemsLayer, id: string): Pt | null => {
	const fixture = electrical.fixtures[id];
	if (fixture) return fixturePlanPoint(graph, fixture);
	const light = electrical.items[id];
	return light ? { x: light.x, y: light.y } : null;
};

/** Вертикальный спуск от потолка к концу кабеля: до прибора на стене — от верха стены, к потолочному светильнику — нет. */
export const cableEndDrop = (graph: WallGraph, electrical: ItemsLayer, id: string): number => {
	const fixture = electrical.fixtures[id];
	if (!fixture) return 0;
	const wall = graph.walls[fixture.wallId];
	return wall ? Math.max(0, wall.height - fixture.elevation) : 0;
};

/** Полная трасса кабеля по плану: от прибора, через точки, к прибору. */
export const cableRoute = (graph: WallGraph, electrical: ItemsLayer, cable: Cable): Pt[] | null => {
	const start = cableEndPoint(graph, electrical, cable.from);
	const end = cableEndPoint(graph, electrical, cable.to);
	if (!start || !end) return null;
	return cableRouteBetween(start, end, cable.points);
};

export const cableRouteBetween = (start: Pt, end: Pt, points: Pt[]): Pt[] => {
	if (points.length === 0) return [start, ...elbowTo(null, start, end), end];
	if (points.length === 1) {
		const corner = points[0];
		const horizontal = { x: end.x, y: start.y };
		const vertical = { x: start.x, y: end.y };
		return [
			start,
			...(dist(corner, horizontal) <= dist(corner, vertical) ? [horizontal] : [vertical]),
			end,
		];
	}
	const first = points[0];
	const last = points[points.length - 1];
	const next = points[1];
	const previous = points[points.length - 2];
	const adjustedFirst =
		Math.abs(first.x - next.x) < 1
			? { x: first.x, y: start.y }
			: Math.abs(first.y - next.y) < 1
				? { x: start.x, y: first.y }
				: first;
	const adjustedLast =
		Math.abs(last.x - previous.x) < 1
			? { x: last.x, y: end.y }
			: Math.abs(last.y - previous.y) < 1
				? { x: end.x, y: last.y }
				: last;
	return [
		start,
		...elbowTo(next, adjustedFirst, start).reverse(),
		adjustedFirst,
		...points.slice(1, -1),
		adjustedLast,
		...elbowTo(previous, adjustedLast, end),
		end,
	];
};

export const polylineLength = (points: Pt[]): number => {
	let total = 0;
	for (let i = 1; i < points.length; i++) total += dist(points[i - 1], points[i]);
	return total;
};

/** Сколько кабеля уйдёт: по потолку плюс вертикальные спуски к приборам на стенах. */
export const cableLength = (
	graph: WallGraph,
	electrical: ItemsLayer,
	cable: Cable,
): { plan: number; drops: number; total: number } | null => {
	const route = cableRoute(graph, electrical, cable);
	if (!route) return null;
	const plan = polylineLength(route);
	const drops =
		cableEndDrop(graph, electrical, cable.from) + cableEndDrop(graph, electrical, cable.to);
	return { plan, drops, total: plan + drops };
};

/**
 * Следующая точка трассы строго по горизонтали или по вертикали от предыдущей:
 * кабель не поворачивает под произвольным углом. Ось выбирается по большему
 * смещению курсора, координата вдоль оси притягивается к сетке.
 */
export const snapOrthogonal = (last: Pt, point: Pt, gridStep: number): Pt => {
	const dx = point.x - last.x;
	const dy = point.y - last.y;
	return Math.abs(dx) >= Math.abs(dy)
		? { x: snapTo(point.x, gridStep), y: last.y }
		: { x: last.x, y: snapTo(point.y, gridStep) };
};

/**
 * Точки, которых не хватает, чтобы дойти от `last` до `target` под прямыми углами.
 * Если цель уже на одной прямой — ничего; иначе один угол. Куда повернуть, решает
 * направление предыдущего отрезка: сначала продолжаем его, потом поворачиваем.
 */
export const elbowTo = (prev: Pt | null, last: Pt, target: Pt): Pt[] => {
	const sameX = Math.abs(target.x - last.x) < 1;
	const sameY = Math.abs(target.y - last.y) < 1;
	if (sameX || sameY) return [];
	const horizontal = prev
		? Math.abs(last.y - prev.y) < 1
		: Math.abs(target.x - last.x) >= Math.abs(target.y - last.y);
	return [horizontal ? { x: target.x, y: last.y } : { x: last.x, y: target.y }];
};
